import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    this.#restoreTrigger();
    this.#triggerObserver = new MutationObserver(this.#restoreTrigger);
    this.#triggerObserver.observe(this, { subtree: true, attributes: true, attributeFilter: ['hidden', 'style', 'open'] });
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener('dialog:close', this.#restoreTrigger);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener('dialog:close', this.#restoreTrigger);
    this.#triggerObserver?.disconnect();
  }

  #restoreTrigger = () => {
    if (this.hidden) this.hidden = false;
    if (this.style.display === 'none') this.style.removeProperty('display');

    const dialog = this.querySelector('dialog');
    if (!dialog) return;
    if (dialog.hidden) dialog.hidden = false;
    if (dialog.style.display === 'none') dialog.style.removeProperty('display');
    if (dialog.open && !dialog.matches(':modal')) {
      dialog.removeAttribute('open');
      requestAnimationFrame(() => this.showDialog());
    }
  };

  #triggerObserver;

  #handleCartAdd = () => {
    this.#restoreTrigger();
    const notice = this.querySelector('.urlife-cart-added-notice');
    if (notice) {
      notice.hidden = false;
      clearTimeout(this.#noticeTimer);
      this.#noticeTimer = setTimeout(() => {
        notice.hidden = true;
      }, 3500);
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
      // Cart section rendering can replace the dialog after this event. Re-open
      // the replacement as a real modal once the new product markup is ready.
      setTimeout(this.#ensureDialogOpen, 250);
      setTimeout(this.#ensureDialogOpen, 900);
    }

    requestAnimationFrame(this.#restoreTrigger);
  };

  #noticeTimer;

  #ensureDialogOpen = () => {
    this.#restoreTrigger();
    const dialog = this.refs.dialog;
    if (!dialog || dialog.matches(':modal')) return;
    if (dialog.open) dialog.removeAttribute('open');
    this.showDialog();
  };

  open() {
    this.#restoreTrigger();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
    requestAnimationFrame(this.#restoreTrigger);
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
